



















import {
    Component,
    Device,
    Inject,
    Prop,
    Vue,
    Watch,
    ComponentStyle,
    ComponentStyleDefinition

} from "@zoovu/runner-browser-api";
import ResizeObserver from "resize-observer-polyfill";

@Component({})
export default class Slider extends Vue {

    @Inject(Device.INJECTION_KEY)
    device: Device;

    @ComponentStyle()
    componentStyle: ComponentStyleDefinition;

    @Prop({type: Number, default: 0})
    min: number;

    @Prop({type: Number, default: 100})
    max: number;

    @Prop({type: Number, default: 1})
    step: number;

    @Prop({type: Number, default: null})
    leftValue: number;

    @Prop({type: Number, default: null})
    rightValue: number;

    @Prop({type: [Number, String]})
    width: string | number;

    @Prop({type: [Number, String], default: 6})
    height: string | number;

    @Prop({type: Number, default: 16})
    dotSize: number;

    @Prop({type: Boolean, default: false})
    leftValueAdjustable: boolean;

    @Prop({type: Boolean, default: true})
    rightValueAdjustable: boolean;

    sliderLeftValue: number = this.leftValue != null ? this.leftValue : this.min;
    sliderRightValue: number = this.rightValue != null ? this.rightValue : this.max;

    isLeftMoving = false;
    isRightMoving = false;

    offsetX: number = 0;
    stepInPixels: number = 1;
    precision = 1;

    resizeObserver: ResizeObserver = new ResizeObserver(this.updateStepAndOffset);

    mounted() {
        this.updatePrecision();
        this.updateStepAndOffset();
        this.bindEvents();
    }

    beforeDestroy() {
        this.unbindEvents();
    }

    bindEvents() {
        document.addEventListener('touchmove', this.onMove, {passive: false});
        document.addEventListener('touchend', this.onMoveEnd, {passive: false});
        document.addEventListener('mousemove', this.onMove);
        document.addEventListener('mouseup', this.onMoveEnd);
        document.addEventListener('mouseleave', this.onMoveEnd);
        this.resizeObserver.observe(this.$refs.sliderElement as Element);
    }

    unbindEvents() {
        document.removeEventListener('touchmove', this.onMove);
        document.removeEventListener('touchend', this.onMoveEnd);
        document.removeEventListener('mousemove', this.onMove);
        document.removeEventListener('mouseup', this.onMoveEnd);
        document.removeEventListener('mouseleave', this.onMoveEnd);
        this.resizeObserver.unobserve(this.$refs.sliderElement as Element);
    }

    @Watch("leftValue")
    onLeftValueChange() {
        if (!this.isLeftMoving) {
            this.sliderLeftValue = this.leftValue;
            this.$emit('left-value-adjusted', this.leftValue);
        }
    }

    @Watch("rightValue")
    onRightValueChange() {
        if (!this.isRightMoving) {
            this.sliderRightValue = this.rightValue;
            this.$emit('right-value-adjusted', this.rightValue);
        }
    }

    @Watch("min")
    onMinChanged() {
        this.updatePrecision();
        this.updateStepAndOffset();
        if (this.leftValue == null) {
            this.sliderLeftValue = this.min;
            this.$emit('left-value-adjusted', this.min);
        }
        if (this.sliderLeftValue < this.min) {
            this.sliderLeftValue = this.min;
            this.$emit('left-value-adjusted', this.min);
        }
    }

    @Watch("max")
    onMaxChanged() {
        this.updatePrecision();
        this.updateStepAndOffset();
        if (this.rightValue == null) {
            this.sliderRightValue = this.max;
            this.$emit('right-value-adjusted', this.max);
        }
        if (this.sliderRightValue > this.max) {
            this.sliderRightValue = this.max;
            this.$emit('right-value-adjusted', this.max);
        }
    }

    onMoveStart(e) {
        const clientX = e.clientX || e.touches[0].clientX;
        const mouseX = clientX - this.offsetX;

        if (!this.leftValueAdjustable && this.rightValueAdjustable) {
            this.isLeftMoving = false;
            this.isRightMoving = true;
        } else if (!this.rightValueAdjustable && this.leftValueAdjustable) {
            this.isLeftMoving = true;
            this.isRightMoving = false;
        } else if (this.rightValueAdjustable && this.leftValueAdjustable) {
            if (mouseX < this.sliderLeftPositionInPixels + (this.sliderRightPositionInPixels - this.sliderLeftPositionInPixels) / 2) {
                this.isLeftMoving = true;
                this.isRightMoving = false;
            } else {
                this.isLeftMoving = false;
                this.isRightMoving = true;
            }
        }

        this.setSliderValueFromMouseX(clientX);
    }

    onMove(e) {
        if (this.isMoving) {
            e.preventDefault();

            if (e.targetTouches && e.targetTouches[0]) {
                e = e.targetTouches[0];
            }

            this.setSliderValueFromMouseX(e.clientX);
        }
    }

    onMoveEnd() {
        if (this.isLeftMoving) {
            this.$emit('left-value-changed', this.sliderLeftValue);
        } else if (this.isRightMoving) {
            this.$emit('right-value-changed', this.sliderRightValue);
        }

        this.isLeftMoving = false;
        this.isRightMoving = false;
    }

    setSliderValueFromMouseX(clientMouseX: number) {
        const mouseX = clientMouseX - this.offsetX;
        const valueIndex = Math.round(mouseX / this.stepInPixels);

        const newValue = ((this.step * this.precision) * valueIndex + (this.min * this.precision)) / this.precision;

        let newSliderValue = Math.min(Math.max(newValue, this.min), this.max);

        if (this.isLeftMoving) {
            newSliderValue = Math.min(newSliderValue, this.sliderRightValue);
            this.sliderLeftValue = newSliderValue;
            this.$emit('left-value-change', newSliderValue);
        } else if (this.isRightMoving) {
            newSliderValue = Math.max(newSliderValue, this.sliderLeftValue);
            this.sliderRightValue = newSliderValue;
            this.$emit('right-value-change', newSliderValue);
        }
    }

    get isMoving(): boolean {
        return this.isLeftMoving || this.isRightMoving;
    }

    get wrapperStyle() {
        const wrapperStyle = {padding: `${this.dotSize / 2}px ${this.dotSize / 2}px`};

        if (this.width) {
            wrapperStyle["width"] = `${this.width}px`;
        }

        return wrapperStyle;
    }

    get selectedRangeStyle() {
        return {
            left: `${this.sliderLeftPositionInPixels}px`,
            width: `${this.sliderRightPositionInPixels - this.sliderLeftPositionInPixels}px`
        };
    }

    get leftDotStyle() {
        return this.getDotStyle(this.sliderLeftPositionInPixels);
    }

    get rightDotStyle() {
        return this.getDotStyle(this.sliderRightPositionInPixels);
    }

    get sliderLeftPositionInPixels(): number {
        return this.valueToPositionInPixels(this.sliderLeftValue);
    }

    get sliderRightPositionInPixels(): number {
        return this.valueToPositionInPixels(this.sliderRightValue);
    }

    getDotStyle(positionInPixels: number) {
        let translateValue = `translateX(${positionInPixels - (this.dotSize / 2)}px)`;

        const dotStyle = {
            width: `${this.dotSize}px`,
            height: `${this.dotSize}px`,
            // @ts-ignore
            top: `${(-(this.dotSize - this.height) / 2)}px`,

            transform: translateValue,
            WebkitTransform: translateValue,
            msTransform: translateValue
        };

        return dotStyle;
    }

    valueToPositionInPixels(value: number): number {
        const valueInPx = Math.round((value - this.min) * this.precision)
            / (this.step * this.precision)
            * this.stepInPixels;
        return this.device.roundToPhysicalPixels(valueInPx);
    }

    updateStepAndOffset() {
        this.offsetX = this.$refs.sliderElement["getBoundingClientRect"]().left;

        // @ts-ignore
        const sliderWidthInPixels = this.$refs.sliderElement["offsetWidth"] ? this.$refs.sliderElement["offsetWidth"] : this.width - this.dotSize;
        const delta = this.max === this.min ? 1 : ((this.max - this.min) / this.step);
        const gap = sliderWidthInPixels / delta;

        this.stepInPixels = gap > 0 ? gap : 1;
    }

    updatePrecision() {
        const stepDecimals = this.getPrecision(this.step);
        const minDecimals = this.getPrecision(this.min);
        const maxDecimals = this.getPrecision(this.max);

        this.precision = Math.max(stepDecimals, minDecimals, maxDecimals);
    }

    getPrecision(field) {
        const decimals = `${field}`.split(".")[1];
        return decimals ? Math.pow(10, decimals.length) : 1;
    }
}
